import {
    Button, CardActions,
    IconButton,
    makeStyles,
    Paper,
    Table, TableBody,
    TableCell, TableContainer,
    TableHead,
    TableRow,
    Toolbar,
    Typography
} from '@material-ui/core';
import React, { useState } from 'react';
import ErrorCardController from "../../UiComponents/ErrorCardController";
import {TrafficShapingService} from "../../../Domain/Services/TrafficShapingService";
import {TrafficShapingStatus} from "../../../Domain/Models/TrafficShapingStatus";
import DecisionModal, {DecisionModalProps} from "../../UiComponents/DecisionModal";
import CircularProgress from '@material-ui/core/CircularProgress';
import SettingsModal from "./SettingsModal";

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
    },
    title: {
        flexGrow: 1
    },
    clickableCell: {
        cursor: 'pointer',
    },
}));

const defaultDecisionModalProps: DecisionModalProps = {
    open: false,
    message: '',
    onAccept: () => {},
    onCancel: () => {},
}

export interface TrafficShapingStatusContentProps
{
    errorCardControllerRef: React.RefObject<ErrorCardController>;
    updateStatus: () => void;
    trafficShapingStatus?: TrafficShapingStatus;
    requestInProgress: boolean;
}

export default function TrafficShapingStatusContent(props: TrafficShapingStatusContentProps)
{
    const classes = useStyles();
    const { errorCardControllerRef, updateStatus, trafficShapingStatus, requestInProgress } = props;

    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [decisionModalProps, setDecisionModalProps] = useState<DecisionModalProps>(defaultDecisionModalProps);

    const handleStartClicked = () =>
    {
        setDecisionModalProps({
            open: true,
            message: 'Are you sure you want to start the Traffic Shaping Service?',
            onAccept: startTrafficShaping,
            onCancel: decisionModalClose,
        })
    }

    const handleStopClicked = () =>
    {
        setDecisionModalProps({
            open: true,
            message: 'Are you sure you want to stop the Traffic Shaping Service?',
            onAccept: stopTrafficShaping,
            onCancel: decisionModalClose,
        })
    }

    const handleClearClicked = () =>
    {
        setDecisionModalProps({
            open: true,
            message: 'Are you sure you want to clear the Traffic Shaping Service queue?',
            onAccept: clearTrafficShaping,
            onCancel: decisionModalClose,
        })
    }
    
    const handleEditClicked = () =>
    {
        setSettingsModalOpen(true);
    }

    const decisionModalClose = () => setDecisionModalProps(defaultDecisionModalProps);

    const startTrafficShaping = () =>
    {
        decisionModalClose();
        TrafficShapingService.start()
            .then(() => updateStatus())
            .catch((e: Error) => errorCardControllerRef.current?.addErrorMessage(e.message))
    }

    const stopTrafficShaping = () =>
    {
        decisionModalClose();
        TrafficShapingService.stop()
            .then(() => updateStatus())
            .catch((e: Error) => errorCardControllerRef.current?.addErrorMessage(e.message))
    }

    const clearTrafficShaping = () =>
    {
        decisionModalClose();
        TrafficShapingService.clear()
            .then(() => updateStatus())
            .catch((e: Error) => errorCardControllerRef.current?.addErrorMessage(e.message))
    }
    
    const getTreeStatus = (): string =>
    {
        if (!trafficShapingStatus?.isRunning)
        {
            return '';
        }
        else if (trafficShapingStatus?.runningWithoutRules)
        {
            return 'Running without rules.';
        }
        else if (trafficShapingStatus?.isTreeCalculated)
        {
            return 'Decision tree calculated.'
        }
        
        return 'Calculating decision tree...';
    }

    return (
        <>
            <DecisionModal {...decisionModalProps} />
            <SettingsModal
              open={settingsModalOpen}
              isRunning={trafficShapingStatus?.isRunning ?? false}
              onClose={() => setSettingsModalOpen(false)}
          />

            <Paper className={classes.paper}>
                <Toolbar>
                    <Typography className={classes.title} component="h2" variant="h6" color="primary" gutterBottom>
                        Traffic Shaping
                    </Typography>
                    {requestInProgress && (
                      <CircularProgress variant="indeterminate" />
                    )}
                </Toolbar>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Running</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Entries in Queue</TableCell>
                                <TableCell>Last Error Message</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow hover>
                                <TableCell>{trafficShapingStatus?.isRunning ? 'Yes' : 'No'}</TableCell>
                                <TableCell>{getTreeStatus()}</TableCell>
                                <TableCell>{trafficShapingStatus?.numberOfEntriesInQueue}</TableCell>
                                <TableCell>{trafficShapingStatus?.lastErrorMessage}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                <CardActions>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={trafficShapingStatus?.isRunning}
                        onClick={handleStartClicked}
                    >
                        Start
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!trafficShapingStatus?.isRunning}
                        onClick={handleStopClicked}
                    >
                        Stop
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={trafficShapingStatus?.isRunning}
                      onClick={handleClearClicked}
                    >
                        Clear
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleEditClicked}
                    >
                        Edit Settings
                    </Button>
                </CardActions>
            </Paper>
        </>
    );
}